import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { ModalTournamentsTabs } from "configs/types";
import { closeModal } from "store/modals/slice";
import {
  getTournamentsStatusList,
  handleClearFields,
  getTournamentItem,
  createTournament,
  updateTournament,
  getTournamentsList,
  handleUpdateFields,
  setTournamentContent,
} from "store/liveOps/tournaments/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import Button from "components/buttons/Button";
import TournamentsMainTab from "../tabs/tournaments/TournamentsMainTab";
import TournamentsRulesTab from "../tabs/tournaments/TournamentsRulesTab";
import TournamentsScoresTab from "../tabs/tournaments/TournamentsScoresTab";
import TournamentsRewardsTab from "../tabs/tournaments/TournamentsRewardsTab";
import TournamentsContentTab from "../tabs/tournaments/TournamentsContentTab";
import TournamentsGamesTab from "../tabs/tournaments/TournamentsGamesTab";

import { Modal } from "components/Modal/Modal";
import styles from "./LiveOpsTournamentsEditModal.module.scss";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const LiveOpsTournamentsEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isItemFetched = useAppSelector((s) => s.tournaments.isItemFetched);
  const isListStatusFetched = useAppSelector(
    (s) => s.tournaments.isListStatusFetched,
  );
  const payload = useAppSelector((s) => s.tournaments.payload);
  const currencies = useAppSelector((s) => s.settings.currencies);
  const locales = useAppSelector((s) => s.settings.locales);
  const isItemFetching = useAppSelector((s) => s.tournaments.isItemFetching);

  const [tabs] = useState<ModalTournamentsTabs[]>([
    ModalTournamentsTabs.main,
    ModalTournamentsTabs.rules,
    ModalTournamentsTabs.scores,
    ModalTournamentsTabs.rewards,
    ModalTournamentsTabs.locales,
    ModalTournamentsTabs.games,
  ]);

  const [activeTab, setActiveTab] = useState(ModalTournamentsTabs.main);
  const [currency, setCurrency] = useState(currencies[0]);
  const [locale, setLocale] = useState(locales[0]);
  const [metric, setMetric] = useState("betAmount");

  const currentItem = payload?.activationRules?.find(
    (el) => el.currency === currency,
  );
  const currentItemScores = payload?.scores?.find((el) => el.metric === metric);
  const currentItemContent = payload?.contents?.find(
    (el) => el.locale === locale,
  );

  const formErrors = useFormErrors();
  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId || payload?.id);

  const permissions = usePermissionHelper();

  useEffect(() => {
    if (!isListStatusFetched) {
      dispatch(getTournamentsStatusList());
    }
  }, [isListStatusFetched]);

  useEffect(() => {
    if (searchParams.get("entityId") && !isItemFetched) {
      dispatch(getTournamentItem(searchParams.get("entityId")!));
    }
  }, [searchParams, isItemFetched]);

  useEffect(() => {
    formErrors.resetErrors();
  }, [locale]);

  const isLoaded = () => {
    if (entityId !== null && !isItemFetched) {
      return false;
    }
    return true;
  };

  const loaded = isLoaded();

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const renderTab = () => {
    if (activeTab === ModalTournamentsTabs.main) {
      return (
        <TournamentsMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!permissions?.update}
        />
      );
    }

    if (activeTab === ModalTournamentsTabs.rules) {
      return (
        <TournamentsRulesTab
          activeCurrency={currency}
          setActiveCurrency={setCurrency}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          clearFieldsErrors={formErrors.clearFieldsErrors}
          readOnly={!permissions?.update}
        />
      );
    }

    if (activeTab === ModalTournamentsTabs.scores) {
      return (
        <TournamentsScoresTab
          activeMetric={metric}
          setActiveMetric={setMetric}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          clearFieldsErrors={formErrors.clearFieldsErrors}
          readOnly={!permissions?.update}
        />
      );
    }

    if (activeTab === ModalTournamentsTabs.rewards) {
      return <TournamentsRewardsTab readOnly={!permissions?.update} fieldErrors={formErrors.errors} clearFieldError={formErrors.clearFieldError} resetErrors={formErrors.resetErrors} />;
    }

    if (activeTab === ModalTournamentsTabs.locales) {
      return (
        <TournamentsContentTab
          activeLocale={locale}
          setActiveLocale={setLocale}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!permissions?.update}
        />
      );
    }

    return <TournamentsGamesTab />;
  };

  const handleApply = async () => {
    if (activeTab === ModalTournamentsTabs.main && payload) {
      if (!isEdit) {
        formErrors.resetErrors();
      }

      try {
        if (isEdit) {
          await dispatch(updateTournament(payload)).unwrap();

          dispatch(
            showNotification({
              title: t("success"),
              message: t("entityUpdateSuccess"),
              type: NotificationTypes.success,
            }),
          );
        } else {
          const newId = await dispatch(createTournament(payload)).unwrap();

          dispatch(
            handleUpdateFields({
              ...payload,
              id: newId,
            }),
          );

          dispatch(
            showNotification({
              title: t("success"),
              message: t("entityCreateSuccess"),
              type: NotificationTypes.success,
            }),
          );

          await dispatch(
            getTournamentsList({
              limit: 10,
              offset: 0,
              title: "",
              statusIds: [],
              currencies: [],
              order: {},
            }),
          );

          navigate(
            `${location.pathname}?activeModal=tournaments&entityId=${newId}`,
          );
        }
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
        return;
      }
    }

    if (activeTab === ModalTournamentsTabs.rules && entityId) {
      try {
        await instance.put(`${apiRoutes.tournamentsSetRules}/${entityId}`, {
          ...currentItem,
          currency,
        });

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }
    }

    if (activeTab === ModalTournamentsTabs.scores && entityId) {
      try {
        await instance.put(`${apiRoutes.tournamentsSetScores}/${entityId}`, {
          ...currentItemScores,
          metric,
          isActive: currentItemScores?.isActive ?? 0,
        });

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }
    }

    if (activeTab === ModalTournamentsTabs.rewards && entityId) {
      formErrors.resetErrors();
      try {
        await instance.put(`${apiRoutes.tournamentsSetRewards}/${entityId}`, {
          rewards: payload?.rewards,
        });

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({ error, t, dispatch, formErrors });
      }

      return;
    }

    if (activeTab === ModalTournamentsTabs.locales && entityId) {
      try {
        await dispatch(
          setTournamentContent({
            tournamentId: Number(entityId),
            payload: {
              locale,
              title: currentItemContent?.title ?? "",
              description: currentItemContent?.description ?? "",
              content: currentItemContent?.content ?? "",
              isActive: currentItemContent?.isActive ?? 0,
              image: currentItemContent?.image,
              imageSmall: currentItemContent?.imageSmall,
              portraitImage: currentItemContent?.portraitImage,
            },
          }),
        ).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }

      return;
    }
  };

  const handleTabClick = (tab: ModalTournamentsTabs) => {
    if (!isEdit && tab !== ModalTournamentsTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("tournament")}</Modal.Header>

      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            canBeClicked={isEdit}
            onClick={handleTabClick}
          />

          <Modal.Content
            className={styles.liveopsTournamentsEditModalContentWrapper}
          >
            {renderTab()}
          </Modal.Content>

          {permissions?.update && activeTab !== ModalTournamentsTabs.games && (
            <Modal.Footer>
              <Button
                isSecondary
                txt={t("cancel")}
                handleClick={handleCloseClick}
              />

              <Button
                isPrimary
                txt={t("save")}
                handleClick={handleApply}
                isLoad={isItemFetching}
              />
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default LiveOpsTournamentsEditModal;
